import { EnumContainerOrderAction, getEnumContainerOrderActionLabel } from '@Model';
import {
    Badge,
    Button,
    FlexColumn,
    FlexRow,
    iconSizeSm,
    iconSizeXs,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconArrowsExchange, IconArrowUp, IconChevronRight, IconPlus, IconRefresh, IconX } from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { actionKindOf } from './containerActionKind';
import tagStyles from './ContainerActionTag.module.css';
import styles from './ContainerActions.module.css';

interface ContainerActionTagProps {
    /** The action of a stored row, or `swap` for the two rows of an exchange. */
    action: EnumContainerOrderAction | 'swap';
}

/** The action of a line as a colored chip with its icon. */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function ContainerActionTag({ action }: ContainerActionTagProps) {
    return (
        <FlexRow
            gap="xxs"
            align="center"
            shrink={false}
            className={`${tagStyles.tag} ${tagStyles[actionKindOf(action)]}`}
        >
            {action === 'swap' ? <IconArrowsExchange size={iconSizeXs} /> : actionIcon(action)}
            {actionLabel(action)}
        </FlexRow>
    );
}

const actionLabel = (action: EnumContainerOrderAction | 'swap') =>
    action === 'swap' ? 'Tauschen' : getEnumContainerOrderActionLabel(action);

function actionIcon(action: EnumContainerOrderAction) {
    switch (action) {
        case EnumContainerOrderAction.Collect:
            return <IconArrowUp size={iconSizeXs} />;
        case EnumContainerOrderAction.Empty:
            return <IconRefresh size={iconSizeXs} />;
        default:
            return <IconPlus size={iconSizeXs} />;
    }
}

interface ContainerActionLineProps {
    /** The action of a stored row, or `swap` for the two rows of an exchange. */
    action: EnumContainerOrderAction | 'swap';
    /** Present while the line may be removed. */
    onRemove?: () => void;
    removeLabel: string;
    /** Why the line no longer fits the order, shown under it. */
    invalid?: string;
    /** The rows of fields of the line. */
    children: ReactNode;
    /** Shown below the fields once the toggle next to the tag is opened. Without it the line has no toggle. */
    details?: ReactNode;
    /** Names what the details hold, as in "Bemerkung anzeigen". */
    detailsLabel?: string;
    /** Whether the closed details hold something the line does not show otherwise. */
    hasDetails?: boolean;
}

/** One line of the container card, labelled with its action: the tag, a toggle for its details, the rows of fields and the remove button. */
// @ai-generated 2026-10-28T13:39:59+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function ContainerActionLine({
    action,
    onRemove,
    removeLabel,
    invalid,
    children,
    details,
    detailsLabel = '',
    hasDetails = false,
}: ContainerActionLineProps) {
    const i18n = useTranslate('');
    const [open, setOpen] = useState(false);

    const toggleLabel = `${i18n(detailsLabel)} ${i18n(open ? 'ausblenden' : 'anzeigen')}${
        hasDetails && !open ? ` (${i18n('Angaben vorhanden')})` : ''
    }`;

    return (
        <FlexRow
            label={actionLabel(action)}
            gap="md"
            align="start"
            className={`${styles.line} ${invalid ? styles.invalidLine : ''}`}
        >
            <FlexRow className={styles.lineSide}>
                <ContainerActionTag action={action} />
            </FlexRow>
            <FlexRow justify="center" className={styles.lineToggle}>
                {details && (
                    <Button
                        label={toggleLabel}
                        tooltip={toggleLabel}
                        appearance="transparent"
                        type="square"
                        size="xxs"
                        onClick={() => setOpen((wasOpen) => !wasOpen)}
                    >
                        <IconChevronRight
                            size={iconSizeSm}
                            className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
                        />
                        {hasDetails && <FlexRow className={styles.toggleMarker} />}
                    </Button>
                )}
            </FlexRow>
            <FlexColumn gap="xs" align="stretch" className={styles.lineBody}>
                {children}
                {details && open && (
                    <FlexRow className={styles.details}>
                        <FlexRow className={styles.detailFields}>{details}</FlexRow>
                    </FlexRow>
                )}
                {invalid && <FlexRow className={styles.invalidHint}>{invalid}</FlexRow>}
            </FlexColumn>
            <FlexRow justify="end" className={styles.lineSide}>
                {onRemove && (
                    <Button
                        label={removeLabel}
                        tooltip="Entfernen"
                        appearance="transparent"
                        type="square"
                        size="xxs"
                        onClick={onRemove}
                    >
                        <IconX size={iconSizeSm} />
                    </Button>
                )}
            </FlexRow>
        </FlexRow>
    );
}

interface ContainerLineFieldsProps {
    /** Names the row inside a line of two, such as "Raus" and "Rein". */
    label?: string;
    type: ReactNode;
    material?: ReactNode;
    /** The number of the container, or the count of a collective type. */
    detail?: ReactNode;
    /** Right of the fields: the booking status and what to do next. */
    status?: ReactNode;
}

/** One row of fields of a line. Every row uses the same grid, so the fields of all lines align. */
export function ContainerLineFields({ label, type, material, detail, status }: ContainerLineFieldsProps) {
    return (
        <FlexRow gap="xs" align="start" className={styles.lineFields}>
            <FlexRow className={styles.rowLabel}>{label && <Badge>{label}</Badge>}</FlexRow>
            <FlexRow gap="xxs" className={styles.lineField}>
                {type}
            </FlexRow>
            <FlexRow gap="xxs" className={styles.lineField}>
                {material}
            </FlexRow>
            <FlexRow gap="xxs" className={styles.lineField}>
                {detail}
            </FlexRow>
            <FlexRow gap="xs" className={styles.lineStatus}>
                {status}
            </FlexRow>
        </FlexRow>
    );
}
